<template>
  <div>
    <Layout ref="layoutRef" :selectedOption="1" />

    <!-- Breadcrumb -->
    <div class="mb-3 row align-items-center">
      <div class="col-11">
        <nav aria-label="breadcrumb">
          <ol class="breadcrumb mb-0">
            <li class="breadcrumb-item"><router-link to="/tramite">Trámites</router-link></li>
            <li class="breadcrumb-item">
              <router-link :to="{ name: 'TramiteForm', params: { p_idtramite: idTramite } }">
                Trámite N.° {{ idTramite }}
              </router-link>
            </li>
            <li class="breadcrumb-item">
              <router-link :to="{ name: 'TramiteForm', params: { p_idtramite: idTramite }, query: { tab: 'reportes' } }">
                Reportes
              </router-link>
            </li>
            <li class="breadcrumb-item active" aria-current="page">Archivos CSV</li>
          </ol>
        </nav>
      </div>
      <div class="col-1 text-end">
        <button class="btn-help" type="button" title="Abrir ayuda" @click="showModalHelp = true">
          <span class="material-symbols-outlined">help</span>
        </button>
      </div>
    </div>

    <!-- Info trámite -->
    <div class="card border-0 mb-3" style="background-color:#e8f4fd;">
      <div class="card-body py-2 px-3">
        <span class="text-dark fw-bold">{{ datosTramite }}</span>
      </div>
    </div>

    <!-- Tabs -->
    <div class="mb-3">
      <div class="nav nav-tabs">
        <button class="nav-link d-flex align-items-center gap-1"
                :class="{ active: activeTab === 'todos' }"
                @click="cambiarTab('todos')">
          <span class="material-symbols-outlined">docs</span> Todos
          <span v-if="dataTodos.length" class="badge bg-secondary ms-1">{{ dataTodos.length }}</span>
        </button>
        <button class="nav-link d-flex align-items-center gap-1"
                :class="{ active: activeTab === 'novedades' }"
                @click="cambiarTab('novedades')">
          <span class="material-symbols-outlined">warning</span> Novedades
          <span v-if="dataNovedades.length" class="badge bg-danger ms-1">{{ dataNovedades.length }}</span>
        </button>
        <button class="nav-link d-flex align-items-center gap-1"
                :class="{ active: activeTab === 'resumen' }"
                @click="cambiarTab('resumen')">
          <span class="material-symbols-outlined">summarize</span> Resumen
          <span v-if="dataResumen.length" class="badge bg-secondary ms-1">{{ dataResumen.length }}</span>
        </button>
      </div>
    </div>

    <!-- Barra de acciones -->
    <div class="mb-3 d-flex gap-2 align-items-center flex-wrap">
      <button class="btn btn-outline-secondary d-flex align-items-center gap-1"
              :disabled="loading" @click="fetchData">
        <span class="material-symbols-outlined">refresh</span>
        {{ loading ? 'Cargando...' : 'Recargar' }}
      </button>
      <button class="btn btn-outline-success d-flex align-items-center gap-1"
              :disabled="filteredData.length === 0" @click="exportExcel">
        <span class="material-symbols-outlined">table_view</span>
        Exportar Excel
      </button>
      <button v-if="hayFiltrosActivos" class="btn btn-outline-danger d-flex align-items-center gap-1"
              @click="limpiarFiltros">
        <span class="material-symbols-outlined">filter_alt_off</span>
        Limpiar filtros
      </button>
      <span class="text-muted small ms-auto">
        {{ filteredData.length }} de {{ displayedData.length }} registros
      </span>
    </div>

    <!-- Estado de carga -->
    <div v-if="loading" class="text-center py-5 text-muted">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="mt-2">Cargando datos...</p>
    </div>

    <!-- Sin datos -->
    <div v-else-if="displayedData.length === 0" class="alert alert-info text-center">
      No hay registros disponibles para este tab.
    </div>

    <!-- Tabla -->
    <div v-else class="table-responsive">
      <table class="table table-sm table-bordered table-hover">
        <thead class="table-dark">
          <!-- Encabezados -->
          <tr>
            <th v-for="col in tableColumns" :key="col" class="text-nowrap">
              {{ columnLabels[col] ?? col }}
            </th>
          </tr>
          <!-- Fila de filtros -->
          <tr class="filtros-row">
            <th v-for="col in tableColumns" :key="'f-' + col" class="p-1">
              <!-- Select para columnas con opciones fijas -->
              <select
                v-if="columnSelectOptions[col]"
                v-model="filtros[col]"
                class="form-select form-select-sm filter-control"
                @change="currentPage = 1"
              >
                <option value="">Todos</option>
                <option v-for="opt in columnSelectOptions[col]" :key="opt" :value="opt">
                  {{ opt }}
                </option>
              </select>
              <!-- Input texto para el resto -->
              <input
                v-else
                v-model="filtros[col]"
                type="text"
                class="form-control form-control-sm filter-control"
                placeholder="Filtrar..."
                @input="currentPage = 1"
              />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="filteredData.length === 0">
            <td :colspan="tableColumns.length" class="text-center text-muted py-3">
              Sin resultados con los filtros aplicados.
            </td>
          </tr>
          <tr v-for="(row, i) in paginatedData" :key="i">
            <td v-for="col in tableColumns" :key="col" class="text-nowrap">
              {{ col === 'nombreMedIns' ? truncateText(row[col]) : row[col] }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Paginación -->
    <div v-if="filteredData.length > 0"
         class="d-flex justify-content-between align-items-center mt-3">
      <span class="text-muted small">
        Página {{ currentPage }} de {{ totalPages }} —
        registros {{ (currentPage - 1) * pageSize + 1 }} a
        {{ Math.min(currentPage * pageSize, filteredData.length) }}
      </span>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-secondary btn-sm"
                :disabled="currentPage === 1" @click="currentPage--">
          ◀ Anterior
        </button>
        <button class="btn btn-outline-secondary btn-sm"
                :disabled="currentPage === totalPages" @click="currentPage++">
          Siguiente ▶
        </button>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import api from '@/services/api'
import Layout from '@/components/Layout.vue'
import Footer from '@/components/Footer.vue'
import * as XLSX from 'xlsx'

// ── Columnas ──────────────────────────────────────────────────────────────────
const tableColumnsOrdered = [
  'idTramite', 'noPlanilla', 'cedula1', 'cedula2', 'nombre',
  'fingreso', 'fsalida', 'diagPri', 'diagSec', 'lmuerto',
  'ftarifario', 'noServicio', 'cantidad', 'vsem', 'vpaciente',
  'noModificador', 'nombreMedIns', 'tipo', 'fechaAtencion',
  'obsCodificadaGD', 'obsComplementariaGD',
  'obsCodificadaAM', 'obsComplementariaAM',
  'obsCodificadaCT', 'obsComplementariaCT',
  'obsCodificadaGDdet', 'obsComplementariaGDdet',
  'obsCodificadaAMdet', 'obsComplementariaAMdet',
  'obsCodificadaCTdet', 'obsComplementariaCTdet',
  'respuestaGD', 'respuestaAM', 'respuestaCT',
  'respuestaGDdet', 'respuestaAMdet', 'respuestaCTdet',
  'plaResolucion', 'detResolucion',
]

const tableColumnsOrderedResumen = [
  'idTramite', 'noPlanilla', 'cedula1', 'cedula2', 'nombre',
  'fingreso', 'fsalida', 'diagPri', 'diagSec', 'vsem',
]

const columnLabels = {
  idTramite:               'ID Trámite',
  noPlanilla:              'N° Planilla',
  cedula1:                 'Cédula 1',
  cedula2:                 'Cédula 2',
  nombre:                  'Nombre',
  fingreso:                'F. Ingreso',
  fsalida:                 'F. Salida',
  diagPri:                 'Diag. Principal',
  diagSec:                 'Diag. Secundario',
  lmuerto:                 'L. Muerto',
  ftarifario:              'F. Tarifario',
  noServicio:              'N° Servicio',
  cantidad:                'Cantidad',
  vsem:                    'V. SEM',
  vpaciente:               'V. Paciente',
  noModificador:           'N° Modificador',
  nombreMedIns:            'Nombre Medicamento/Insumo',
  tipo:                    'Tipo',
  fechaAtencion:           'Fecha Atención',
  obsCodificadaGD:         'Obs. Codificada GD',
  obsComplementariaGD:     'Obs. Complementaria GD',
  obsCodificadaAM:         'Obs. Codificada AM',
  obsComplementariaAM:     'Obs. Complementaria AM',
  obsCodificadaCT:         'Obs. Codificada CT',
  obsComplementariaCT:     'Obs. Complementaria CT',
  obsCodificadaGDdet:      'Obs. Codificada GD (Det.)',
  obsComplementariaGDdet:  'Obs. Complementaria GD (Det.)',
  obsCodificadaAMdet:      'Obs. Codificada AM (Det.)',
  obsComplementariaAMdet:  'Obs. Complementaria AM (Det.)',
  obsCodificadaCTdet:      'Obs. Codificada CT (Det.)',
  obsComplementariaCTdet:  'Obs. Complementaria CT (Det.)',
  respuestaGD:             'Respuesta GD',
  respuestaAM:             'Respuesta AM',
  respuestaCT:             'Respuesta CT',
  respuestaGDdet:          'Respuesta GD (Det.)',
  respuestaAMdet:          'Respuesta AM (Det.)',
  respuestaCTdet:          'Respuesta CT (Det.)',
  plaResolucion:           'Justificación Pla',
  detResolucion:           'Justificación Det',
}

// Columnas con opciones fijas de filtro (select en lugar de input)
const columnSelectOptions = {
  plaResolucion: ['JUSTIFICADA', 'SE ACEPTA OBJECIÓN'],
  detResolucion: ['JUSTIFICADA', 'SE ACEPTA OBJECIÓN'],
}

// ── Estado ────────────────────────────────────────────────────────────────────
const route       = useRoute()
const layoutRef   = ref(null)
const idTramite   = computed(() => Number(route.params.p_idtramite) || 0)
const activeTab   = ref('todos')
const dataTodos     = ref([])
const dataNovedades = ref([])
const dataResumen   = ref([])
const currentPage   = ref(1)
const pageSize      = ref(10)
const loading       = ref(false)
const showModalHelp = ref(false)
const datosTramite  = ref(sessionStorage.getItem('datosTramite') || '')

// Filtros reactivos — un valor por columna
const filtros = reactive({})

// ── Computed ──────────────────────────────────────────────────────────────────
const tableColumns = computed(() =>
  activeTab.value === 'resumen' ? tableColumnsOrderedResumen : tableColumnsOrdered
)

const displayedData = computed(() => ({
  todos:     dataTodos.value,
  novedades: dataNovedades.value,
  resumen:   dataResumen.value,
}[activeTab.value] ?? dataTodos.value))

// Datos filtrados según los valores activos en filtros
const filteredData = computed(() => {
  return displayedData.value.filter(row =>
    tableColumns.value.every(col => {
      const filtro = filtros[col]
      if (!filtro) return true
      const valor = String(row[col] ?? '').toLowerCase()
      // Select → coincidencia exacta; input → contiene
      if (columnSelectOptions[col]) {
        return String(row[col] ?? '') === filtro
      }
      return valor.includes(filtro.toLowerCase())
    })
  )
})

const hayFiltrosActivos = computed(() =>
  tableColumns.value.some(col => filtros[col])
)

const totalPages = computed(() =>
  Math.max(1, Math.ceil(filteredData.value.length / pageSize.value))
)

const paginatedData = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  return filteredData.value.slice(start, start + pageSize.value)
})

// ── Métodos ───────────────────────────────────────────────────────────────────
const cambiarTab = (tab) => {
  activeTab.value   = tab
  currentPage.value = 1
  limpiarFiltros()
}

const limpiarFiltros = () => {
  tableColumns.value.forEach(col => { filtros[col] = '' })
  currentPage.value = 1
}

const truncateText = (text) => {
  if (typeof text !== 'string') return ''
  return text.length > 20 ? text.slice(0, 20) + '...' : text
}

const mapearFilas = (data, columns) =>
  data.map(row => {
    const obj = {}
    columns.forEach(col => { obj[col] = row[col] ?? '' })
    return obj
  })

const fetchData = async () => {
  loading.value = true
  try {
    const [resTodos, resNovedades, resResumen] = await Promise.all([
      api.get(`/archivos-datos/json/${idTramite.value}`),
      api.get(`/archivos-datos/json/errores/${idTramite.value}`),
      api.get(`/archivos-datos/json/resumen/${idTramite.value}`),
    ])
    dataTodos.value     = mapearFilas(resTodos.data,     tableColumnsOrdered)
    dataNovedades.value = mapearFilas(resNovedades.data, tableColumnsOrdered)
    dataResumen.value   = mapearFilas(resResumen.data,   tableColumnsOrderedResumen)
    currentPage.value   = 1
    limpiarFiltros()
  } catch (err) {
    console.error('Error al cargar datos:', err)
  } finally {
    loading.value = false
  }
}

// Exporta los datos ya filtrados
const exportExcel = () => {
  if (filteredData.value.length === 0) return
  const columns = activeTab.value === 'resumen'
    ? tableColumnsOrderedResumen
    : tableColumnsOrdered
  const headers  = columns.map(col => columnLabels[col] ?? col)
  const dataRows = filteredData.value.map(row => columns.map(col => row[col] ?? ''))
  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...dataRows])
  const workbook  = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, activeTab.value)
  XLSX.writeFile(workbook, `archivo_${activeTab.value}_${idTramite.value}.xlsx`)
}

onMounted(async () => {
  layoutRef.value?.toggleProcesos()
  layoutRef.value?.toggleReportes()
  layoutRef.value?.toggleParametros()
  layoutRef.value?.toggleAyuda()
  await fetchData()
})
</script>

<style scoped>
.btn-help {
  background: none; border: none; padding: 0;
  cursor: pointer; color: #0d6efd;
}
.btn-help .material-symbols-outlined { font-size: 32px; transition: color 0.2s; }
.btn-help:hover .material-symbols-outlined { color: #0a58ca; }

.nav-tabs .nav-link {
  color: #0d6efd; display: flex; align-items: center; gap: 0.4rem;
}
.nav-tabs .nav-link.active {
  background-color: #0d6efd; color: #fff !important;
  border-color: #0d6efd #0d6efd #fff;
}
.nav-tabs .nav-link:hover:not(.active) { background-color: #e9ecef; }
.material-symbols-outlined { font-size: 1.2rem; }

/* Fila de filtros */
.filtros-row { background-color: #2c3e50; }
.filtros-row th { padding: 4px !important; }
.filter-control {
  font-size: 0.75rem;
  min-width: 60px;
  background-color: #fff;
  border-color: #495057;
}
.filter-control:focus { box-shadow: 0 0 0 2px rgba(13,110,253,.35); }
</style>